import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type Node,
  type Edge,
  Panel,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import { ArrowLeft, Plus, Save, Rocket, Loader2, MessageSquare, Tag, ArrowRightLeft, Clock, FileText, CircleDot, Pencil, Variable, Square, HelpCircle, Handshake, Bot } from "lucide-react";
import { automationNodeTypes } from "./nodes/AutomationNodeTypes";
import { resolveTriggerFromNode } from "@/lib/automation-flow";
import { syncCampaignStatusForAutomation } from "@/lib/automation-campaign";

const MAX_NODES = 20;

const ADD_NODE_OPTIONS = [
  { type: "send_message", label: "Enviar Mensagem", icon: MessageSquare, category: "Respostas" },
  { type: "delay", label: "Delay", icon: Clock, category: "Respostas" },
  { type: "condition", label: "Condição", icon: HelpCircle, category: "Condições" },
  { type: "add_tag", label: "Adicionar Tag", icon: Tag, category: "CRM" },
  { type: "remove_tag", label: "Remover Tag", icon: Tag, category: "CRM" },
  { type: "move_funnel", label: "Mover Funil", icon: ArrowRightLeft, category: "CRM" },
  { type: "move_column", label: "Mover Coluna", icon: ArrowRightLeft, category: "CRM" },
  { type: "add_comment", label: "Comentário Interno", icon: FileText, category: "CRM" },
  { type: "create_task", label: "Criar Tarefa", icon: CircleDot, category: "CRM" },
  { type: "update_lead", label: "Atualizar Lead", icon: Pencil, category: "CRM" },
  { type: "create_deal", label: "Criar Negociação", icon: Handshake, category: "CRM" },
  { type: "set_ai_agent", label: "Pausar/Reativar IA", icon: Bot, category: "CRM" },
  { type: "set_variable", label: "Definir Variável", icon: Variable, category: "Controle" },
  { type: "end", label: "Fim", icon: Square, category: "Controle" },
];

interface AutomationBuilderProps {
  automationId: string;
}

export function AutomationBuilder({ automationId }: AutomationBuilderProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [automationName, setAutomationName] = useState("");
  const [automationStatus, setAutomationStatus] = useState("");
  const [automationTrigger, setAutomationTrigger] = useState<{
    trigger_type: string;
    trigger_config: Record<string, unknown>;
  }>({ trigger_type: "keyword", trigger_config: {} });

  // Callback passed into each node via data to update config
  const handleNodeConfigChange = useCallback((nodeId: string, newConfig: Record<string, unknown>) => {
    setNodes(prev => prev.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, config: newConfig } } : n
    ));
  }, [setNodes]);

  // Load automation data
  useEffect(() => {
    if (!automationId || !workspaceId) return;

    const load = async () => {
      setLoading(true);
      try {
        // Load automation
        const { data: automation } = await supabase
          .from("automations")
          .select("name, status, trigger_type, trigger_config")
          .eq("id", automationId)
          .eq("workspace_id", workspaceId)
          .single();

        if (!automation) {
          toast.error("Automação não encontrada");
          navigate("/automations");
          return;
        }

        const autoRow = automation as {
          name: string;
          status: string;
          trigger_type: string;
          trigger_config: Record<string, unknown>;
        };
        setAutomationName(autoRow.name);
        setAutomationStatus(autoRow.status);
        setAutomationTrigger({
          trigger_type: autoRow.trigger_type || "keyword",
          trigger_config: autoRow.trigger_config || {},
        });

        // Load nodes
        const { data: dbNodes } = await supabase
          .from("automation_nodes")
          .select("id, node_type, position_x, position_y, config, label")
          .eq("automation_id", automationId);

        // Load edges
        const { data: dbEdges } = await supabase
          .from("automation_edges")
          .select("id, source_node_id, target_node_id, branch")
          .eq("automation_id", automationId);

        const flowNodes: Node[] = (dbNodes || []).map((n: any) => {
          let config = (n.config as Record<string, unknown>) || {};
          if (n.node_type === "trigger" && !config.trigger_type) {
            config = {
              ...config,
              trigger_type: autoRow.trigger_type,
              ...autoRow.trigger_config,
            };
          }
          return {
            id: n.id,
            type: n.node_type,
            position: { x: n.position_x, y: n.position_y },
            data: { label: n.label, config, onConfigChange: handleNodeConfigChange, workspaceId },
          };
        });

        const flowEdges: Edge[] = (dbEdges || []).map((e: any) => ({
          id: e.id,
          source: e.source_node_id,
          target: e.target_node_id,
          label: e.branch !== "default" ? e.branch : undefined,
          animated: true,
          style: { stroke: "hsl(var(--primary))", strokeWidth: 2 },
        }));

        setNodes(flowNodes);
        setEdges(flowEdges);
      } catch (err) {
        console.error("Error loading automation:", err);
        toast.error("Erro ao carregar automação");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [automationId, workspaceId]);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => addEdge({ ...connection, animated: true, style: { stroke: "hsl(var(--primary))", strokeWidth: 2 } }, eds));
    },
    [setEdges]
  );

  const handleAddNode = useCallback((type: string, label: string) => {
    if (nodes.length >= MAX_NODES) {
      toast.error(`Limite de ${MAX_NODES} blocos atingido`);
      return;
    }

    const newNode: Node = {
      id: `temp_${Date.now()}`,
      type,
      position: {
        x: 250 + Math.random() * 100,
        y: (nodes.length + 1) * 120,
      },
      data: { label, config: {}, onConfigChange: handleNodeConfigChange, workspaceId },
    };

    setNodes(prev => [...prev, newNode]);
  }, [nodes, setNodes]);

  const handleSave = useCallback(async (): Promise<{ idMap: Map<string, string>; savedNodes: Array<{ id: string; type: string; config: Record<string, unknown>; label?: string }>; savedEdges: Array<{ source: string; target: string; branch: string }> } | null> => {
    if (!workspaceId || !automationId) return null;
    setSaving(true);

    try {
      const { data: existingNodes } = await supabase
        .from("automation_nodes")
        .select("id")
        .eq("automation_id", automationId);
      const existingNodeIds = (existingNodes || []).map((n: { id: string }) => n.id);

      // Capture current node data before any async state updates
      const currentNodes = [...nodes];
      const currentEdges = [...edges];

      // Insert all nodes
      const nodeInserts = currentNodes.map(n => ({
        automation_id: automationId,
        workspace_id: workspaceId,
        node_type: n.type,
        position_x: n.position.x,
        position_y: n.position.y,
        config: (n.data as any)?.config || {},
        label: (n.data as any)?.label || null,
      }));

      const { data: insertedNodes, error: nodesError } = await supabase
        .from("automation_nodes")
        .insert(nodeInserts as any)
        .select("id");

      if (nodesError) throw nodesError;

      // Map old IDs to new IDs
      const idMap = new Map<string, string>();
      currentNodes.forEach((n, i) => {
        idMap.set(n.id, (insertedNodes as any[])[i].id);
      });

      // Insert edges with mapped IDs
      const edgeInserts = currentEdges
        .filter(e => idMap.has(e.source) && idMap.has(e.target))
        .map(e => ({
          automation_id: automationId,
          workspace_id: workspaceId,
          source_node_id: idMap.get(e.source)!,
          target_node_id: idMap.get(e.target)!,
          branch: (e as any).label || "default",
        }));

      if (edgeInserts.length > 0) {
        const { error: edgesError } = await supabase
          .from("automation_edges")
          .insert(edgeInserts as any);
        if (edgesError) throw edgesError;
      }

      const newNodeIds = (insertedNodes as { id: string }[]).map((n) => n.id);
      const staleNodeIds = existingNodeIds.filter((id) => !newNodeIds.includes(id));
      if (staleNodeIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("automation_nodes")
          .delete()
          .in("id", staleNodeIds);
        if (deleteError) throw deleteError;
      }

      // Build savedNodes and savedEdges with correct IDs and configs
      const savedNodes = currentNodes.map((n, i) => ({
        id: (insertedNodes as any[])[i].id,
        type: n.type!,
        config: (n.data as any)?.config || {},
        label: (n.data as any)?.label,
      }));

      const savedEdges = currentEdges
        .filter(e => idMap.has(e.source) && idMap.has(e.target))
        .map(e => ({
          source: idMap.get(e.source)!,
          target: idMap.get(e.target)!,
          branch: (e as any).label || "default",
        }));

      // Update node IDs in state to reflect real IDs
      setNodes(prev =>
        prev.map((n, i) => ({
          ...n,
          id: (insertedNodes as any[])[i]?.id || n.id,
        }))
      );

      // Update edge IDs in state to reflect mapped IDs
      setEdges(prev =>
        prev.map(e => ({
          ...e,
          source: idMap.get(e.source) || e.source,
          target: idMap.get(e.target) || e.target,
        }))
      );

      toast.success("Automação salva");
      return { idMap, savedNodes, savedEdges };
    } catch (err: any) {
      console.error("Save error:", err);
      toast.error(err.message || "Erro ao salvar");
      return null;
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, automationId, workspaceId]);

  const validateFlow = useCallback((): string[] => {
    const errors: string[] = [];

    // Must have trigger
    const hasTrigger = nodes.some(n => n.type === "trigger");
    if (!hasTrigger) errors.push("Falta um bloco de gatilho");

    // Must have end
    const hasEnd = nodes.some(n => n.type === "end");
    if (!hasEnd) errors.push("Falta um bloco de fim");

    // Check disconnected nodes
    const connectedNodeIds = new Set<string>();
    edges.forEach(e => {
      connectedNodeIds.add(e.source);
      connectedNodeIds.add(e.target);
    });
    const disconnected = nodes.filter(n => !connectedNodeIds.has(n.id));
    if (disconnected.length > 0 && nodes.length > 1) {
      errors.push(`${disconnected.length} bloco(s) desconectado(s)`);
    }

    // Check spam (multiple send_message without delay)
    let consecutiveSends = 0;
    // Simple heuristic: count send_message nodes
    const sendCount = nodes.filter(n => n.type === "send_message").length;
    const delayCount = nodes.filter(n => n.type === "delay").length;
    if (sendCount > 3 && delayCount === 0) {
      errors.push("Spam potencial: muitas mensagens sem delay");
    }

    const triggerNode = nodes.find((n) => n.type === "trigger");
    const triggerType =
      (triggerNode?.data as { config?: { trigger_type?: string } })?.config?.trigger_type ||
      automationTrigger.trigger_type;
    const keywordTypes = ["keyword", "story_reply", "comment"];
    if (keywordTypes.includes(triggerType)) {
      const keywords =
        (triggerNode?.data as { config?: { keywords?: string[] } })?.config?.keywords ||
        (automationTrigger.trigger_config?.keywords as string[] | undefined) ||
        [];
      if (!Array.isArray(keywords) || keywords.length === 0) {
        errors.push("Gatilho por palavra-chave exige ao menos uma palavra-chave");
      }
    }

    return errors;
  }, [nodes, edges, automationTrigger]);

  const handlePublish = useCallback(async () => {
    const errors = validateFlow();
    if (errors.length > 0) {
      errors.forEach(e => toast.error(e));
      return;
    }

    setPublishing(true);
    try {
      // Save first and get saved data with correct IDs
      const saveResult = await handleSave();
      if (!saveResult) {
        toast.error("Falha ao salvar antes de publicar");
        return;
      }

      // Compile flow using saved data (not stale React state)
      const compiledFlow = {
        nodes: saveResult.savedNodes,
        edges: saveResult.savedEdges,
      };

      const triggerNode = compiledFlow.nodes.find((n) => n.type === "trigger");
      const resolvedTrigger = resolveTriggerFromNode(triggerNode, automationTrigger);

      const { data: currentAuto } = await supabase
        .from("automations")
        .select("published_version")
        .eq("id", automationId)
        .single();

      const nextVersion = ((currentAuto as { published_version?: number } | null)?.published_version ?? 0) + 1;

      const triggerUpdate: Record<string, unknown> = {
        compiled_flow: compiledFlow,
        status: "active",
        published_at: new Date().toISOString(),
        published_version: nextVersion,
        trigger_type: resolvedTrigger.trigger_type,
        trigger_config: resolvedTrigger.trigger_config,
        max_executions_per_contact: resolvedTrigger.max_executions_per_contact,
      };

      const { error } = await supabase
        .from("automations")
        .update(triggerUpdate as any)
        .eq("id", automationId);

      if (error) throw error;

      await syncCampaignStatusForAutomation(supabase, automationId, "active").catch(() => {});

      setAutomationStatus("active");
      toast.success("Automação publicada e ativa!");
    } catch (err: any) {
      console.error("Publish error:", err);
      toast.error(err.message || "Erro ao publicar");
    } finally {
      setPublishing(false);
    }
  }, [nodes, edges, automationId, automationTrigger, handleSave, validateFlow]);

  const groupedOptions = useMemo(() => {
    const groups: Record<string, typeof ADD_NODE_OPTIONS> = {};
    ADD_NODE_OPTIONS.forEach(opt => {
      if (!groups[opt.category]) groups[opt.category] = [];
      groups[opt.category].push(opt);
    });
    return groups;
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-64px)]">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/automations")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
          </Button>
          <span className="text-sm font-medium truncate max-w-[200px]">{automationName}</span>
          {automationStatus && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              automationStatus === "active" ? "bg-green-500/10 text-green-600" :
              automationStatus === "draft" ? "bg-muted text-muted-foreground" :
              "bg-yellow-500/10 text-yellow-600"
            }`}>
              {automationStatus === "active" ? "Ativa" : automationStatus === "draft" ? "Rascunho" : "Pausada"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{nodes.length}/{MAX_NODES} blocos</span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {Object.entries(groupedOptions).map(([category, options]) => (
                <div key={category}>
                  <DropdownMenuLabel className="text-xs">{category}</DropdownMenuLabel>
                  {options.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <DropdownMenuItem
                        key={opt.type}
                        onClick={() => handleAddNode(opt.type, opt.label)}
                        className="gap-2"
                      >
                        <Icon className="h-4 w-4" /> {opt.label}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Salvar
          </Button>

          <Button size="sm" onClick={handlePublish} disabled={publishing}>
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />}
            Publicar
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={automationNodeTypes}
          fitView
          deleteKeyCode={["Backspace", "Delete"]}
          className="bg-muted/30"
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} className="!bg-background" />
          <Controls className="!bg-card !border-border !shadow-sm [&>button]:!bg-card [&>button]:!text-foreground [&>button]:!border-border [&>button]:!fill-foreground" />
          <MiniMap
            className="!bg-card !border-border !shadow-sm"
            nodeStrokeWidth={3}
            nodeColor={(node) => {
              const colors: Record<string, string> = {
                trigger: "#d97706",
                condition: "#2563eb",
                send_message: "#16a34a",
                add_tag: "#9333ea",
                remove_tag: "#dc2626",
                move_funnel: "#4f46e5",
                move_column: "#0891b2",
                add_comment: "#ca8a04",
                create_task: "#0d9488",
                update_lead: "#ea580c",
                delay: "#475569",
                set_variable: "#db2777",
                end: "#ef4444",
              };
              return colors[node.type || ""] || "#6b7280";
            }}
            maskColor="hsl(0, 0%, 0%, 0.12)"
          />
        </ReactFlow>
      </div>
    </div>
  );
}
